/**
 * Users Service
 * API calls for user management endpoints
 */

import { snowflakeApi } from '../utils/api';

// AuthGuard Resource User types
export interface AuthGuardUserProfile {
  tenantId: string;
  userEntraId: string;
  globalSettings: {
    language: string;
    timeZone: string;
    country: string;
  };
  profileInformation: {
    profileName: string;
    profileId: string;
    profileNameLowercase: string;
    accessType: string;
  };
  personalInformation?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface AuthGuardResourceUsersResponse {
  success: boolean;
  resource: string;
  count: number;
  users: AuthGuardUserProfile[];
}

// Role User types
export interface RoleUser {
  id: number;
  userId: string;
  firstName: string;
  lastName: string;
  userEmail: string;
}

// Search User types (for user search)
export interface SearchUser {
  id: number;
  userId: string;
  firstName: string;
  lastName: string;
  userEmail: string;
}

export interface RoleUsersResponse {
  success: boolean;
  roleName: string;
  count: number;
  sharedUsers: RoleUser[];
}

/**
 * Users API service for user management
 * All requests automatically include the AuthGuard API token via snowflakeApi
 */
export const usersService = {
  /**
   * Get all users assigned to a specific AuthGuard resource
   * @param resource - The resource ID (default: 'serp')
   * @param includePersonalInformation - Include personal info (default: true)
   */
  async getAuthGuardResourceUsers(
    resource: string = 'serp',
    includePersonalInformation: boolean = true
  ): Promise<AuthGuardResourceUsersResponse> {
    const params = new URLSearchParams();
    params.append('includepersonalinformation', includePersonalInformation.toString());
    
    const response = await snowflakeApi.get(
      `/api/v1/auth/users/resource/${resource}?${params.toString()}`
    );
    
    return response.data;
  },

  /**
   * Get all users assigned to a specific role by role name
   * @param roleName - The name of the role (e.g., 'Administrator', 'Data App Builder')
   */
  async getUsersByRoleName(roleName: string): Promise<RoleUsersResponse> {
    // URL encode the role name to handle spaces and special characters
    const encodedRoleName = encodeURIComponent(roleName);
    
    const response = await snowflakeApi.get(
      `/api/v1/auth/users/role/${encodedRoleName}`
    );
    
    return response.data;
  },

  /**
   * Get all roles with their users
   * Common role names: Administrator, Data App Builder, Tag Manager, etc.
   */
  async getAllRolesWithUsers(roleNames: string[]): Promise<Record<string, RoleUsersResponse>> {
    const results: Record<string, RoleUsersResponse> = {};
    
    // Fetch all roles in parallel
    const promises = roleNames.map(async (roleName) => {
      try {
        const data = await this.getUsersByRoleName(roleName);
        results[roleName] = data;
      } catch (error) {
        console.error(`Failed to fetch users for role "${roleName}":`, error);
        results[roleName] = {
          success: false,
          roleName,
          count: 0,
          sharedUsers: [],
        };
      }
    });
    
    await Promise.all(promises);
    return results;
  },

  /**
   * Search for users in Guardrail
   * @param searchTerm - Search term for user name or email
   */
  async searchUsers(searchTerm: string = ''): Promise<SearchUser[]> {
    const response = await snowflakeApi.get(
      `/api/v1/auth/users/search?searchTerm=${encodeURIComponent(searchTerm)}`
    );
    
    return response.data;
  },

  /**
   * Assign a user to an AuthGuard resource
   * @param email - User's email address
   * @param resourceId - Resource ID (e.g., 'serp')
   * @param application - Application ID (e.g., 'serp')
   * @param resourceType - Resource type (defaults to resourceId)
   * @param accessType - Access type (defaults to 'User')
   * @param permissions - Permissions object (defaults to {})
   */
  async assignUserToResource(
    email: string,
    resourceId: string = 'serp',
    application: string = 'serp',
    resourceType?: string,
    accessType: string = 'User',
    permissions: object = {}
  ): Promise<any> {
    const response = await snowflakeApi.post('/api/v1/auth/users/assign-to-resource', {
      email,
      resourceId,
      application,
      resourceType: resourceType || resourceId,
      accessType,
      permissions,
    });
    
    return response.data;
  },

  /**
   * Assign a user to a role by role name
   * @param roleName - The name of the role (e.g., 'Data App Builder')
   * @param email - User's email address
   */
  async assignUserToRole(roleName: string, email: string): Promise<any> {
    const encodedRoleName = encodeURIComponent(roleName);
    
    const response = await snowflakeApi.post(
      `/api/v1/auth/users/role/${encodedRoleName}/assign`,
      { email }
    );
    
    return response.data;
  },

  /**
   * Remove a user from a role by role name
   * @param roleName - The name of the role (e.g., 'Data App Builder')
   * @param email - User's email address
   */
  async removeUserFromRole(roleName: string, email: string): Promise<any> {
    const encodedRoleName = encodeURIComponent(roleName);
    
    const response = await snowflakeApi.delete(
      `/api/v1/auth/users/role/${encodedRoleName}/remove`,
      { data: { email } }
    );
    
    return response.data;
  },

  /**
   * Delete a user profile by profile ID from a specific resource
   * @param profileId - The profile ID to delete
   * @param resourceId - The resource ID (e.g., 'serp')
   */
  async deleteProfile(profileId: string, resourceId: string = 'serp'): Promise<any> {
    const response = await snowflakeApi.delete(
      `/api/v1/auth/users/profile/${profileId}?resource=${resourceId}`
    );
    
    return response.data;
  },
};

// Export individual functions for convenience (backward compatibility)
export const getAuthGuardResourceUsers = usersService.getAuthGuardResourceUsers.bind(usersService);
export const getUsersByRoleName = usersService.getUsersByRoleName.bind(usersService);
export const getAllRolesWithUsers = usersService.getAllRolesWithUsers.bind(usersService);
export const searchUsers = usersService.searchUsers.bind(usersService);
export const assignUserToResource = usersService.assignUserToResource.bind(usersService);
export const assignUserToRole = usersService.assignUserToRole.bind(usersService);
export const removeUserFromRole = usersService.removeUserFromRole.bind(usersService);
export const deleteProfile = (profileId: string, resourceId?: string) => 
  usersService.deleteProfile(profileId, resourceId);
